"use client";

/*

  return viewport dimensions (x & y) of an DOM element by its id

*/
import { useCallback, useState, useLayoutEffect } from "react";
import { getElementDimsById } from "../dom";
import { debounce } from "../delay";
import type { TPositionDims } from "@hsr/types";

const initialDims: TPositionDims = {
  x: 0,
  y: 0,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  width: 0,
  height: 0
};

export const useElementBoundingClientRect = (
  elementId: string
): TPositionDims => {
  const [elementDims, setElementDims] = useState<TPositionDims>(initialDims);

  const handleResize = useCallback(() => {
    const elDims = getElementDimsById(elementId);

    setElementDims({
      x: elDims?.x ?? 0,
      y: elDims?.y ?? 0,
      top: elDims?.top ?? 0,
      bottom: elDims?.bottom ?? 0,
      left: elDims?.left ?? 0,
      right: elDims?.right ?? 0,
      width: elDims?.width ?? 0,
      height: elDims?.height ?? 0
    });
  }, [elementId]);

  useLayoutEffect(() => {
    handleResize();

    // Debounce to avoid the function fire multiple times
    const handleResizeDebounced = debounce(handleResize, 250);
    window.addEventListener("resize", handleResizeDebounced);

    return () => {
      window.removeEventListener("resize", handleResizeDebounced);
      setElementDims(initialDims);
    };
  }, [elementId, handleResize]);

  return elementDims;
};
